import type { SchoolRecord } from './schoolData.js';

export type SchoolVariant = 'primary' | 'secondary' | 'ip' | 'ip-ib' | 'mixed';

/**
 * Local (MOE) schools that follow the IB (International Baccalaureate)
 * curriculum. International schools are also IB but are identified by their
 * `isInternational` flag (see `isIbSchool`), so they aren't listed here.
 *
 * Note: SOTA follows the IB despite `ipInd === false` in the MOE dataset.
 */
const LOCAL_IB_SCHOOL_SLUGS = new Set<string>([
  'anglo-chinese-school-independent',
  'st-josephs-institution',
  'school-of-the-arts-singapore',
]);

/**
 * Whether a school follows the IB curriculum.
 *
 * Two groups qualify: the fixed set of local IB schools above, and every
 * curated international school (all of which offer the IB Diploma — see
 * `internationalSchoolData.ts`). IB schools get the 'ip-ib' content variant and
 * appear on `/ib` + `/sec-X-ib`.
 */
export function isIbSchool(school: SchoolRecord): boolean {
  return (
    LOCAL_IB_SCHOOL_SLUGS.has(school.slug) || school.isInternational === true
  );
}

/**
 * Resolve the content variant for a school page.
 *
 * Priority chain (order matters):
 * 1. IB school (local IB or international) → 'ip-ib'
 * 2. Mixed-level (primary + secondary) → 'mixed'
 * 3. IP (non-IB) → 'ip'
 * 4. Secondary → 'secondary'
 * 5. Everything else → 'primary'
 *
 * The IB check is first and does NOT require `ipInd`, so IB schools that are not
 * MOE IP schools (SOTA, international schools) still get the IB page.
 *
 * SAP and GEP flags do NOT create separate variants — they are mentioned
 * inline in the page body using the base variant template.
 */
export function resolveSchoolVariant(school: SchoolRecord): SchoolVariant {
  if (isIbSchool(school)) {
    return 'ip-ib';
  }
  if (school.servesPrimary && school.servesSecondary) {
    return 'mixed';
  }
  if (school.ipInd) {
    return 'ip';
  }
  if (school.servesSecondary) {
    return 'secondary';
  }
  return 'primary';
}
